import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { SiSap, SiSalesforce } from "react-icons/si";
import { BrandOracleMark } from "../components/site/BrandOracle";
import Reveal from "../components/site/Reveal";
import { api } from "../lib/api";

const PLATFORM_META = {
  SAP: { Icon: SiSap, color: "text-xp-text" },
  Oracle: { Icon: BrandOracleMark, color: "text-xp-text" },
  Salesforce: { Icon: SiSalesforce, color: "text-xp-text" },
};

const FILTERS = ["All", "SAP", "Oracle", "Salesforce"];

const CaseStudies = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/case-studies")
      .then((r) => mounted && setItems(r.data || []))
      .catch((e) => mounted && setErr(e?.message || "Failed to load"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    if (filter === "All") return items;
    return items.filter((c) => c.platform === filter);
  }, [items, filter]);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[45vh] md:min-h-[55vh] overflow-hidden pt-24 md:pt-32 pb-12 md:pb-20 border-b border-white/5" data-testid="case-studies-hero">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="container-xp relative">
          <div className="eyebrow-lg mb-5">Case Studies</div>
          <h1 className="font-display tracking-display text-[40px] md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="case-studies-title">
            Proof, not pitch decks.
          </h1>
          <p className="mt-8 text-xp-muted max-w-2xl">
            Real engagements, real metrics. A selection of the revenue platforms we have implemented across telecommunications,
            SaaS, utilities and media.
          </p>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="section-xp" data-testid="case-studies-grid-section">
        <div className="container-xp">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
            <div className="flex flex-wrap items-center gap-2" role="tablist" data-testid="case-studies-filters">
              {FILTERS.map((f) => {
                const isActive = filter === f;
                return (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`filter-${f.toLowerCase()}`}
                    className={`px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest border transition-all ${
                      isActive
                        ? "bg-xp-cyan text-xp-bg border-xp-cyan"
                        : "border-white/10 text-xp-muted hover:text-xp-text hover:border-white/25"
                    }`}
                  >
                    {f}
                  </button>
                );
              })}
            </div>
            <div className="font-mono text-xs text-xp-dim">
              {loading ? "loading…" : `${filtered.length} result${filtered.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {loading && (
            <div className="py-20 flex items-center justify-center text-xp-muted" data-testid="case-studies-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-3" /> Loading case studies…
            </div>
          )}

          {err && !loading && (
            <div className="py-10 text-sm text-red-400 font-mono" data-testid="case-studies-error">Error: {err}</div>
          )}

          {!loading && !err && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="case-studies-grid">
              {filtered.map((c, i) => {
                const Meta = PLATFORM_META[c.platform] || { Icon: SiSap };
                return (
                  <Reveal
                    key={c.id}
                    delay={(i % 4) * 0.05}
                    className="group relative overflow-hidden border border-white/10 rounded-sm bg-xp-surface"
                    data-testid={`case-study-${c.id}`}
                  >
                    <div className="grid md:grid-cols-5">
                      <div className="md:col-span-2 relative h-56 md:h-full">
                        <div
                          className="absolute inset-0 opacity-80 transition-transform duration-700 group-hover:scale-105"
                          style={{ backgroundImage: `url('${c.image}')`, backgroundSize: "cover", backgroundPosition: "center" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-xp-bg/80 via-xp-bg/20 to-transparent" />
                        <div className="absolute top-4 left-4 flex items-center gap-2 text-xs font-mono text-xp-text">
                          <Meta.Icon className="h-4 w-4" /> {c.platform}
                        </div>
                      </div>
                      <div className="md:col-span-3 p-8 lg:p-10">
                        <div className="eyebrow mb-3">{c.industry}</div>
                        <h3 className="font-display tracking-display text-2xl lg:text-3xl">
                          {c.title}
                        </h3>
                        <p className="mt-4 text-sm text-xp-muted">{c.summary}</p>

                        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
                          {c.metrics.slice(0, 4).map((m) => (
                            <div key={m.label}>
                              <div className="font-display text-xl text-xp-cyan">{m.value}</div>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-xp-dim mt-1">
                                {m.label}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-6 flex flex-wrap gap-2">
                          {c.tags.slice(0, 4).map((t) => (
                            <span key={t} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-white/10 text-xp-muted">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-xp border-t border-white/5">
        <div className="container-xp">
          <Reveal className="relative overflow-hidden border border-white/10 rounded-sm p-10 md:p-16 bg-xp-surface">
            <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
            <div className="relative grid md:grid-cols-5 gap-10 items-center">
              <div className="md:col-span-3">
                <div className="eyebrow mb-4">Your case study, next</div>
                <h3 className="font-display text-3xl md:text-5xl tracking-display">
                  Let's ship the one that ends up here.
                </h3>
              </div>
              <div className="md:col-span-2 flex md:justify-end">
                <Link to="/contact" className="btn-cyan" data-testid="case-studies-cta">
                  Start a project <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
